import {
  Box,
  Flex,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { CustomModal } from '../../components/custom';
import {
  CheckCircleIcon,
  ChevronUpIcon,
  PlusInCircleIcon,
} from '../../components/icons';
import {
  restoreLocalStorageSnapshot,
  snapshotPersistentPromptKeys,
} from '../../helpers/persistentLocalStorage';
import { getTitleCase } from '../../helpers/utilityFunctions';
import useGetAvailableBalance from '../../hooks/useGetAvailableBalance';
import useGetBusinessDetails from '../../hooks/useGetBusinessDetails';
import AddNewBusinessConfirmation from '../../modules/Dashboard/AddNewBusiness/AddNewBusinessConfirmation';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getAccountBalanceAsync } from '../../store/slices/account/getAccountBalanceSlice';
import { getAccountsAsync } from '../../store/slices/account/getAccountsSlice';
import { devAccessAsync } from '../../store/slices/auth/devAuthSlice';
import { getUserDetailsForBusinessAsync } from '../../store/slices/business/getUserDetailsForBusinessSlice';
import {
  getOutletsAsync,
  IGetOutletsResponse,
} from '../../store/slices/outlet/getOutlets';
import { useAppLayoutOffset } from './AppLayoutContext';

export default function SidebarAccounts() {
  const layoutOffset = useAppLayoutOffset();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { status: accountsStatus } = useAppSelector((state) => state.accounts);
  const { value: authValue } = useAppSelector((state) => state.auth);

  const { status: businessesStatus, value: businessesValue } = useAppSelector(
    (state) => state.userBusinesses
  );

  const { status: outletsStatus, value: outletsValue } = useAppSelector(
    (state) => state.getOutlets
  );
  const { status: userDetailsStatus, value: userDetailsValue } = useAppSelector(
    (state) => state.userDetailsForBusinessFetch
  );

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const businessIdFromStorage = localStorage.getItem('businessId');
  const businessNameFromStorage = localStorage.getItem('businessName');

  const userHasAccountFromStorage = localStorage.getItem('userHasAccount');

  const [businessId, setBusinessId] = useState(
    businessIdFromStorage || businessesValue?.data?.[0]?.id || ''
  );
  const [businessName, setBusinessName] = useState(
    businessNameFromStorage ?? businessesValue?.data?.[0]?.name ?? ''
  );

  const [, setFilteredOutlets] = useState<IGetOutletsResponse['data']>([]);

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const pageAction = searchParams.get('action');

  const isAddNewBusinessPage = pageAction?.includes('add_new_business');

  const userHasPin = authValue?.data?.user?.hasPin;

  const userHasAccount = authValue?.data?.user?.businesses?.find(
    (business) => business?.id === businessId
  )?.hasAccount;

  const handleAddNewBusiness = () => {
    setIsAddModalOpen(true);
  };

  const handleChangeBusiness = useCallback(
    ({
      businessId,
      businessName,
      reloadPage = true,
    }: {
      businessId: string;
      businessName: string;
      reloadPage?: boolean;
    }) => {
      setBusinessId(businessId);
      setBusinessName(businessName);

      const storeBusinessInfoInStorage = () => {
        localStorage.setItem('businessId', businessId);
        localStorage.setItem('businessName', businessName);
      };

      storeBusinessInfoInStorage();

      if (userHasPin) {
        localStorage.setItem('userHasPin', 'true');
      }

      if (userHasAccount) {
        localStorage.setItem('userHasAccount', 'true');
      }

      if (reloadPage) {
        const otherAuthData = localStorage.getItem('otherAuthData') as string;
        const userId = localStorage.getItem('userId');
        const promptSnapshot = snapshotPersistentPromptKeys();

        localStorage.clear();
        restoreLocalStorageSnapshot(promptSnapshot);
        localStorage.setItem('otherAuthData', otherAuthData);
        localStorage.setItem('userId', userId as string);

        if (userHasPin) {
          localStorage.setItem('userHasPin', 'true');
        }

        if (userHasAccount) {
          localStorage.setItem('userHasAccount', 'true');
        }

        if (
          businessesValue?.data?.find((business) => business.id === businessId)
            ?.hasAccount
        ) {
          if (location.pathname !== '/app/dashboard')
            navigate('/app/dashboard');
        } else {
          if (location.pathname !== '/app/get-started') {
            navigate('/app/get-started');
          }
        }

        storeBusinessInfoInStorage();

        window.location.reload();
      }
    },
    [userHasAccount, userHasPin, businessesValue, location, navigate]
  );

  useEffect(() => {
    if (userHasAccount && userHasAccountFromStorage === null) {
      localStorage.setItem('userHasAccount', 'true');
    } else if (!userHasAccount && userHasAccountFromStorage) {
      localStorage.removeItem('userHasAccount');
    }
  }, [userHasAccount, userHasAccountFromStorage]);

  useEffect(() => {
    if (
      !businessNameFromStorage &&
      businessesStatus === 'success' &&
      !isAddNewBusinessPage
    ) {
      handleChangeBusiness({
        businessId: businessesValue?.data?.[0]?.id ?? '',
        businessName: businessesValue?.data?.[0]?.name ?? '',
        reloadPage: false,
      });
    }
  }, [
    businessIdFromStorage,
    businessNameFromStorage,
    businessesStatus,
    businessesValue?.data,
    handleChangeBusiness,
    isAddNewBusinessPage,
  ]);

  useEffect(() => {
    if (businessId) {
      dispatch(getAccountsAsync());
      dispatch(getOutletsAsync());
      dispatch(getUserDetailsForBusinessAsync());
      dispatch(getAccountBalanceAsync());
    }
  }, [dispatch, businessId]);

  useEffect(() => {
    const developerIdInStorage = localStorage.getItem('developerId');
    const devTokenInStorage = localStorage.getItem('devTokenData');

    if (developerIdInStorage && !devTokenInStorage) {
      dispatch(devAccessAsync());
    }
  }, [businessId, dispatch]);

  const closeAddModal = () => {
    setIsAddModalOpen(false);
  };

  const availableBalanceInNaira = useGetAvailableBalance();

  const accountNumberFromStorage = localStorage.getItem('accountNumber') ?? '';

  useEffect(() => {
    if (availableBalanceInNaira === null) dispatch(getAccountBalanceAsync());
  }, [availableBalanceInNaira, dispatch]);

  const businessDetails = useGetBusinessDetails();

  const isBusinessOwner =
    businessDetails?.creatorType?.toLowerCase() === 'owner';

  useEffect(() => {
    if (outletsStatus === 'success') {
      if (isBusinessOwner) {
        setFilteredOutlets(outletsValue?.data);
      } else if (userDetailsStatus === 'success') {
        if (userDetailsValue?.data?.outlets && outletsValue?.data) {
          const filtered = userDetailsValue?.data?.outlets.filter(
            (userOutlet) =>
              outletsValue?.data?.some(
                (outlet) => outlet?.id === userOutlet?.id
              )
          );
          setFilteredOutlets(filtered);
        } else {
          setFilteredOutlets([]);
        }
      }
    }
  }, [
    isBusinessOwner,
    outletsStatus,
    outletsValue?.data,
    userDetailsStatus,
    userDetailsValue?.data?.outlets,
  ]);

  return businessesStatus === 'success' && !!businessesValue?.data?.length ? (
    <Box
      position="fixed"
      top={`${layoutOffset + 8}px`}
      bg="#FAFAFA"
      w={{ base: '70%', md: '192px' }}
      pt="5"
      pb="3"
    >
      <Popover>
        <PopoverTrigger>
          <Box
            h="68px"
            py={3}
            px={4}
            bg="#1A1C1F"
            borderRadius="8px"
            cursor="pointer"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              gap={2}
              alignItems="center"
            >
              <Box>
                <Text
                  fontSize=".875rem"
                  lineHeight="150%"
                  fontWeight={700}
                  noOfLines={1}
                  color="#fff"
                  mb="0.5"
                >
                  {getTitleCase(businessName)}
                </Text>
                <Skeleton
                  height={
                    !!accountNumberFromStorage || accountsStatus === 'loading'
                      ? '21px'
                      : ''
                  }
                  isLoaded={
                    !!accountNumberFromStorage || accountsStatus !== 'loading'
                  }
                  borderRadius="4px"
                  startColor="gray.300"
                  endColor="gray.400"
                >
                  <Text
                    fontSize=".875rem"
                    fontWeight={400}
                    color="gray.300"
                    noOfLines={1}
                  >
                    {accountNumberFromStorage}
                  </Text>
                </Skeleton>
              </Box>

              <IconButton
                variant="ghost"
                _hover={{ background: 'transparent' }}
                icon={<ChevronUpIcon />}
                aria-label="Select Outlet"
              />
            </Box>
          </Box>
        </PopoverTrigger>
        <PopoverContent
          borderRadius="8px"
          bg="#1A1C1F"
          p="2"
          pb="1.5"
          width={{ base: '88vw', sm: '93vw', md: '192px' }}
          maxH="50vh"
          overflowY="scroll"
        >
          <Box
            fontSize=".75rem"
            lineHeight="200%"
            color="gray.200"
            fontWeight={400}
          >
            <Flex
              gap="8px"
              ml="4px"
              cursor="pointer"
              onClick={handleAddNewBusiness}
            >
              <Box>
                <PlusInCircleIcon />
              </Box>
              <Text mt="2px" mb="4px">
                Add New Business
              </Text>
            </Flex>
            {businessesValue?.data?.map((business, index) => (
              <Box key={business?.id}>
                <Box
                  px="2"
                  py="1"
                  borderRadius="4px"
                  transition="all 0.2s"
                  cursor="pointer"
                  _hover={{ background: 'gray.400', color: '#fff' }}
                  onClick={() =>
                    handleChangeBusiness({
                      businessId: business.id ?? '',
                      businessName: business.name ?? '',
                    })
                  }
                  mt="1px"
                >
                  {getTitleCase(business?.name)}
                  {business.name === businessName && (
                    <CheckCircleIcon sx={{ display: 'initial' }} />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </PopoverContent>
      </Popover>

      <CustomModal isOpen={isAddModalOpen} onClose={closeAddModal}>
        <AddNewBusinessConfirmation onClose={closeAddModal} />
      </CustomModal>
    </Box>
  ) : null;
}
